import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
} from 'lucide-react'
import { AuthLayout } from '../../components/layout/AuthLayout'
import { api, extractApiError } from '../../lib/api'
import {
  confirmPasswordRefine,
  passwordStrongSchema,
} from '../../lib/validators'

const schema = z
  .object({
    newPassword: passwordStrongSchema,
    confirmPassword: z.string({ message: 'Confirm Password is required.' }),
  })
  .superRefine(confirmPasswordRefine('newPassword', 'confirmPassword'))

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const [done, setDone] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  // Missing-or-empty token: the link is malformed. Show a static
  // panel rather than letting the user fill the form only to fail
  // server-side.
  if (!token) {
    return (
      <AuthLayout
        centered
        title="Invalid Reset Link"
        subtitle="This reset link is missing a token. Request a new one from the Forgot Password page."
      >
        <div className="rounded-xl border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-4 text-sm text-[color:var(--color-ink)] flex items-start gap-3">
          <AlertTriangle
            size={18}
            className="text-[color:var(--color-danger)] shrink-0 mt-0.5"
          />
          <p className="m-0">
            Reset links expire after a limited time and can only be used once.
          </p>
        </div>
        <p className="mt-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          <Link
            to="/forgot-password"
            className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
          >
            Request new link
          </Link>
        </p>
      </AuthLayout>
    )
  }

  async function onSubmit(values) {
    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword: values.newPassword,
      })
      setDone(true)
      toast.success('Password reset successfully.')
      // Send the user to sign-in after a short pause so they can read
      // the confirmation. They have to sign in fresh because the
      // reset also revoked every existing session.
      window.setTimeout(() => {
        navigate('/sign-in', { replace: true })
      }, 1500)
    } catch (err) {
      const { message, fields } = extractApiError(err)
      if (fields?.token) {
        // Token-class errors are terminal — surface as a banner, not
        // a per-field error, since the form has no token field.
        toast.error(fields.token)
      } else if (fields?.newPassword) {
        form.setError('newPassword', { message: fields.newPassword })
      } else {
        toast.error(message)
      }
    }
  }

  if (done) {
    return (
      <AuthLayout
        centered
        title="Password Reset"
        subtitle="Your password has been updated. Redirecting you to sign in…"
      >
        <div className="rounded-xl border border-[color:var(--color-success-border)] bg-[color:var(--color-success-bg)] p-4 text-sm text-[color:var(--color-ink)] flex items-start gap-3">
          <CheckCircle2
            size={18}
            className="text-[color:var(--color-success)] shrink-0 mt-0.5"
          />
          <p className="m-0">
            All your existing sessions have been signed out for security.
            Use the new password to sign in again.
          </p>
        </div>
      </AuthLayout>
    )
  }

  const newError = form.formState.errors.newPassword?.message
  const confirmError = form.formState.errors.confirmPassword?.message
  const submitting = form.formState.isSubmitting

  return (
    <AuthLayout
      centered
      title="Set a New Password"
      subtitle="Choose a new password for your account. The new password must be different from your current one."
    >
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="space-y-5"
      >
        <PasswordInput
          id="newPassword"
          label="New Password"
          required
          autoComplete="new-password"
          placeholder="Enter a New Password"
          visible={showNew}
          onToggleVisible={() => setShowNew((v) => !v)}
          error={newError}
          register={form.register('newPassword')}
        />
        <PasswordInput
          id="confirmPassword"
          label="Confirm Password"
          required
          autoComplete="new-password"
          placeholder="Re-enter Password"
          visible={showConfirm}
          onToggleVisible={() => setShowConfirm((v) => !v)}
          error={confirmError}
          register={form.register('confirmPassword')}
        />

        <button
          type="submit"
          disabled={submitting}
          className="group w-full h-12 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 shadow-[0_8px_20px_-6px_rgba(2,54,123,0.35)] hover:shadow-[0_12px_28px_-6px_rgba(2,54,123,0.45)] transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          style={{
            background:
              'linear-gradient(135deg, #02367B 0%, #006CA5 50%, #0496C7 100%)',
          }}
        >
          {submitting ? (
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : null}
          Reset Password
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>

        <p className="text-center text-sm text-[color:var(--color-ink-muted)]">
          Remembered it?{' '}
          <Link
            to="/sign-in"
            className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
          >
            Back to Sign In
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}

/** Lock-prefix password input with eye toggle — same visual contract
 *  as the Sign In + Sign Up auth forms. */
function PasswordInput({
  id,
  label,
  required,
  autoComplete,
  placeholder,
  visible,
  onToggleVisible,
  error,
  register,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
      >
        {label}
        {required ? (
          <span
            aria-hidden="true"
            className="ml-0.5 text-[color:var(--color-priority-urgent)]"
          >
            *
          </span>
        ) : null}
      </label>
      <div className="relative">
        <Lock
          size={16}
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
        />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength={128}
          className={
            'w-full h-11 pl-10 pr-10 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
            (error
              ? 'border-[color:var(--color-priority-urgent)]'
              : 'border-[color:var(--color-rule)]')
          }
          {...register}
        />
        <button
          type="button"
          onClick={onToggleVisible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          {visible ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      </div>
      {error ? (
        <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
          {error}
        </p>
      ) : null}
    </div>
  )
}
